import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook para gerenciar o fluxo de salvamento com login
 * Facilita a implementação em qualquer componente que precise salvar dados
 */
export function useSaveWithLogin() {
  const { user } = useAuth();
  const [pendingData, setPendingData] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Versão simplificada que aceita apenas uma função
   * @param {Function} saveFunction - Função que salva os dados (deve retornar Promise)
   * @returns {Promise<boolean>} - true se salvou, false se precisou de login
   */
  const saveWithLoginSimple = useCallback(async (saveFunction) => {
    // Se usuário está logado, salva diretamente
    if (user) {
      setIsLoading(true);
      try {
        await saveFunction();
        return true;
      } catch (error) {
        console.error('Erro ao salvar:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }

    // Se não está logado, preserva função e mostra modal
    setPendingData({ data: null, saveFunction });
    setShowLoginModal(true);
    return false;
  }, [user]);

  /**
   * Tenta salvar dados, solicitando login se necessário
   * @param {Object} data - Dados a serem salvos
   * @param {Function} saveFunction - Função que salva os dados (deve retornar Promise)
   * @returns {Promise<boolean>} - true se salvou, false se precisou de login
   */
  const saveWithLogin = useCallback(async (data, saveFunction) => {
    // Se usuário está logado, salva diretamente
    if (user) {
      setIsLoading(true);
      try {
        await saveFunction(data);
        return true;
      } catch (error) {
        console.error('Erro ao salvar:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }

    // Se não está logado, preserva dados e mostra modal
    setPendingData({ data, saveFunction });
    setShowLoginModal(true);
    return false;
  }, [user]);

  /**
   * Executado após login bem-sucedido
   */
  const handleLoginSuccess = useCallback(async () => {
    setShowLoginModal(false);
    
    if (pendingData) {
      setIsLoading(true);
      try {
        if (pendingData.data !== null) {
          // Versão com dados
          await pendingData.saveFunction(pendingData.data);
        } else {
          // Versão simples
          await pendingData.saveFunction();
        }
        setPendingData(null);
        return true;
      } catch (error) {
        console.error('Erro ao salvar após login:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }
    return false;
  }, [pendingData]);

  /**
   * Cancela o processo de salvamento
   */
  const cancelSave = useCallback(() => {
    setShowLoginModal(false);
    setPendingData(null);
  }, []);

  return {
    saveWithLogin,
    saveWithLoginSimple,
    handleLoginSuccess,
    cancelSave,
    showLoginModal,
    isLoading,
    hasPendingData: !!pendingData
  };
}

// Exemplo de uso:
/*
function RoadmapEditor() {
  const {
    saveWithLogin,
    handleLoginSuccess,
    cancelSave,
    showLoginModal,
    isLoading
  } = useSaveWithLogin();

  const handleSave = async () => {
    try {
      const roadmapData = getCurrentRoadmapData();
      
      const saved = await saveWithLogin(roadmapData, async (data) => {
        // Sua função de salvamento
        const response = await fetch('/api/roadmap-save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        
        if (!response.ok) throw new Error('Erro ao salvar');
        return response.json();
      });

      if (saved) {
        toast.success('Roadmap salvo com sucesso!');
      }
    } catch (error) {
      toast.error('Erro ao salvar roadmap');
    }
  };

  return (
    <div>
      <button 
        onClick={handleSave}
        disabled={isLoading}
        className="btn-primary"
      >
        {isLoading ? 'Salvando...' : 'Salvar Roadmap'}
      </button>

      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={cancelSave}
        feature="saveRoadmap"
        onLoginSuccess={handleLoginSuccess}
        onRegisterSuccess={handleLoginSuccess}
      />
    </div>
  );
}
*/
